import type { ChipTone } from "@outpave/ds";
import ApiKeyRole from "enums/ApiKeyRole";
import ApiKeyStatus from "enums/ApiKeyStatus";

/**
 * The one place an API key's status and role become a label and a tone.
 *
 * Both columns used to print the raw enum, so `ACTIVE` and `ReadOnly` reached
 * the screen untranslated in every locale (the same shape as the relationship
 * and payment-status findings in DS_MIGRATION.md). These return translation
 * KEYS, so the `t()` stays at the call site and no stored value reaches one.
 */
const STATUS_LABEL_KEY: Record<ApiKeyStatus, string> = {
  [ApiKeyStatus.ACTIVE]: "Active",
  [ApiKeyStatus.EXPIRED]: "Expired",
  [ApiKeyStatus.REVOKED]: "Revoked",
};

/**
 * Expiry is a lapse someone has to act on; a revocation is a decision that has
 * already been carried out, so it reports rather than warns.
 */
const STATUS_TONE: Record<ApiKeyStatus, ChipTone> = {
  [ApiKeyStatus.ACTIVE]: "positive",
  [ApiKeyStatus.EXPIRED]: "warning",
  [ApiKeyStatus.REVOKED]: "neutral",
};

const ROLE_LABEL_KEY: Record<ApiKeyRole, string> = {
  [ApiKeyRole.Admin]: "Admin",
  [ApiKeyRole.ReadOnly]: "Read Only",
};

/**
 * The fall-through is "Unknown Status", as RenderUserStatus uses: a status this
 * map has not heard of is unknown, and naming it Revoked would tell a reader a
 * live key had been deactivated.
 */
export const apiKeyStatusLabelKey = (status: ApiKeyStatus): string =>
  STATUS_LABEL_KEY[status] ?? "Unknown Status";

export const apiKeyStatusTone = (status: ApiKeyStatus): ChipTone =>
  STATUS_TONE[status] ?? "neutral";

export const apiKeyRoleLabelKey = (role: ApiKeyRole): string =>
  ROLE_LABEL_KEY[role] ?? ROLE_LABEL_KEY[ApiKeyRole.ReadOnly];
